import { useEffect } from "react";
import "./App.css";
import "./index.css";
import { Toaster } from "sonner";
import axios from "axios";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import YandexMetrika from './components/analytics/yandexMetrica';
import SEO from './components/SEO/SEO';

import {
    Header,
    Hero,
    CompetenceAndTopicsSection,
    ProcessSection,
    ServicesSection,
    TestimonialsSection,
    FAQSection,
    BlogTeaser,
    CTASection,
    Footer,
    TextScrollEffect,
    DiplomasSection,
    TestsSection,
    BookingSection
} from "./components/SiteUI/index"; // Corrected import path

import ArticlePage from "./pages/ArticlePage";
import AuthPage from "./pages/AuthPage";
import ArticlesPage from "./pages/ArticlesPage";

import { useScrollMemory } from "@/hooks/useScrollMemory";
import { ProfilePage } from "./pages/ProfilePage";
import AdminPage from "./pages/admin/AdminPage";
import { AdminUserPage } from "./pages/admin/AdminUserPage";
import { VerificationPage } from "./pages/auth/VerificationPage";
import { ResetPasswordPage } from "./pages/auth/ResetPasswordPage";
import { ChangePasswordPage } from "./pages/profile/ChangePasswordPage";
import { Page404 } from "./pages/Page404";
import { AddPostPage } from "./pages/admin/AddPostPage";
import { SmiTest } from "./pages/tests/SmiTest";
import { YoungTest } from "./pages/tests/YoungTest";
import { BeckTest } from "./pages/tests/BeckTest";
import { TestsPage } from "@/pages/tests/TestsPage";
import { ResultPage } from "./pages/tests/ResultPage";
import { SchedulerPage } from "./components/admin/SchedulerPage";
import { UsersList } from "./components/admin/UsersList";
import { ArticleList } from "./components/admin/ArticleList";
import { TestList } from "./components/test/TestList";
import { ProfileScheduler } from "./components/profile/ProfileScheduler";
import SitemapPage from "./pages/SitemapPage";
import SitemapDebugPage from "./pages/SitemapDebugPage";
import SitemapAutoUpdater from "./components/admin/SitemapAutoUpdater";
import APITestPage from "./pages/APITestPage";
import SitemapTestPage from "./pages/SitemapTestPage";
import SitemapUpdater from "./pages/SitemapUpdater";
import AllArticlesViewer from "./pages/AllArticlesViewer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


function MetrikaTracker() {
    const location = useLocation();
    const COUNTER_ID = 104649855; // ID вашего счетчика

    useEffect(() => {
        if (window.ym) {
            window.ym(COUNTER_ID, 'hit', location.pathname + location.search);
        }
    }, [location.pathname, location.search]); // Срабатывает при каждой смене URL

    return null;
}


function LandingPage() {
    useScrollMemory();

    return (
        <>
            <SEO 
                title="Психологическая консультация в Минске"
                description="Профессиональная психологическая помощь в Минске. Индивидуальные консультации, психологические тесты, поддержка в сложных жизненных ситуациях. Запишитесь на прием к опытному психологу."
                keywords="психолог минск, психологическая консультация, психологическая помощь, психотерапия минск, консультация психолога, психолог онлайн, семейный психолог, детский психолог"
                url="/"
                structuredData={{
                    "@context": "https://schema.org",
                    "@type": "ProfessionalService",
                    "name": "Психологическая консультация в Минске",
                    "description": "Профессиональная психологическая помощь в Минске. Индивидуальные консультации, психологические тесты, поддержка в сложных жизненных ситуациях.",
                    "url": "https://cope.by",
                    "telephone": "+375445686972",
                    "address": {
                        "@type": "PostalAddress",
                        "addressLocality": "Минск",
                        "addressCountry": "BY"
                    },
                    "geo": {
                        "@type": "GeoCoordinates",
                        "latitude": "53.9045",
                        "longitude": "27.5615"
                    },
                    "openingHours": "Mo-Su 09:00-21:00",
                    "priceRange": "$$",
                    "serviceType": "Психологическая консультация"
                }}
            />
            <div className="min-h-screen bg-white text-olive-900">
            <Header />
            <main className="pt-19">
                <Hero />
                <ServicesSection />
                <ProcessSection />
                <TestsSection />
                <CompetenceAndTopicsSection />
                <BookingSection />
                <TestimonialsSection />
                <FAQSection />
                <BlogTeaser />
                <DiplomasSection />
                <CTASection />
            </main>
            <Footer />
            </div>
        </>
    );
}

function App() {
    useEffect(() => {
        (async () => {
            try {
                const response = await axios.get(`${API}/`);
                console.log(response.data.message);
            } catch (e) {
                console.warn("Backend /api/ not reachable yet — that's OK for mock stage.");
            }
        })();
    }, []);

    return (
        <HelmetProvider>
            <BrowserRouter>
                <YandexMetrika />
                <MetrikaTracker />
                <div className="min-h-screen bg-olive-50 text-foreground">
                    <Routes>
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/articles" element={<ArticlesPage />} />
                        <Route path="/article/:slug" element={<ArticlePage />} />
                    <Route path="/auth" element={<AuthPage />} />
                    <Route path="/auth/verify/:id/:token" element={<VerificationPage />} />
                    <Route path="/auth/reset-password/:id/:token" element={<ResetPasswordPage />} />
                    <Route path="/profile" element={<ProfilePage />}>
                        <Route index element={<ProfileScheduler />} />
                        <Route path="tests" element={<TestList />} />
                    </Route>
                    <Route path="/profile/change-password" element={<ChangePasswordPage />} />
                    <Route path="/admin" element={<AdminPage />}>
                        <Route index element={<SchedulerPage />} />
                        <Route path="users" element={<UsersList />} />
                        <Route path="articles" element={<ArticleList />} />
                        <Route path="sitemap" element={<SitemapAutoUpdater />} />
                        <Route path="users/:id" element={<AdminUserPage />} />
                        <Route path="post" element={<AddPostPage />} />
                        <Route path="article/:slug" element={<AddPostPage />} />
                    </Route>
                    <Route path="/tests">
                        <Route index element={<TestsPage />} />
                        <Route path="smi" element={<SmiTest />} />
                        <Route path="young" element={<YoungTest />} />
                        <Route path="beck" element={<BeckTest />} />
                        <Route path="results/:id" element={<ResultPage />} />
                    </Route>

                    <Route path="/sitemap.xml" element={<SitemapPage />} />
                    <Route path="/sitemap-debug" element={<SitemapDebugPage />} />
                    <Route path="/sitemap-test" element={<SitemapTestPage />} />
                    <Route path="/sitemap-updater" element={<SitemapUpdater />} />
                    <Route path="/all-articles" element={<AllArticlesViewer />} />
                    <Route path="/api-test" element={<APITestPage />} />
                    <Route path="*" element={<Page404 />} />
                    </Routes>
                    <Toaster richColors position="top-right" />
                </div>
            </BrowserRouter>
        </HelmetProvider>
    );
}

export default App;
